<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      Manage Template Categories
    </template>

    <template #body>
      <div class="form-group row">
        <div class="form-group mr-2 mb-0 col-12 d-flex">
          <router-link :to="{ name: 'admin_communication_template_categories_new' }" class="btn btn-primary btn-sm ml-auto">
            Add New Category
          </router-link>
        </div>
      </div>

      <PortalTable table-id="template_categories_index" 
        ref="template_categories_index"
        :table-data="records"
        :total-records="totalRecords"
        :page="pageNumber"
        :page-count="pageCount"
        :loading="isSavableStateProcessing"
        :filter-values="filterValues"
        :add-filter-to-route="true"
        filter-display="row"
        empty="No Template categories found!"
        @on-sort="handleSortChange"
        @on-page="handlePageChange"
        @on-filter-change="onFilterChange">
        <template #columns>
          <Column field="category_name"
            header="Category Name"
            sortable
            filter-field="category_name"
            :show-filter-menu="false">
            <template #filter="{ filterModel = { value: null }, filterCallback }">
              <InputText ref="category_name" v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search by Category name"
                @input="filterCallback()" />
            </template>
          </Column>
          <Column field="category_template_name" header="Category Type" 
            sort-field="category_type"
            sortable
            filter-field="category_type"
            :show-filter-menu="false"
            :filter-menu-style="{ width: '15rem' }">
            <template #body="slotProps">
              {{ translateCategoryType(slotProps.data.category_template_name) }}
            </template>
            <template #filter>
              <MultiSelectFilter ref="category_type"
                filter-field="category_type"
                option-value="key"
                class="form-control form-control-select"
                option-label="value"
                :options="getCategoryTypes"
                @change="onChange" />
            </template>
          </Column>
          <Column field="created_by.full_name" 
            header="Created By" 
            sortable />
          <Column field="updated_by.full_name" 
            header="Last Edited By"
            sortable />
          <Column field="state"
            header="State"
            sort-field="state"
            sortable
            filter-field="state"
            :show-filter-menu="false"
            :filter-menu-style="{ width: '15rem' }">
            <template #body="slotProps">
              {{ slotProps.data.disabled_at ? 'Disabled' : 'Enabled' }}
            </template>
            <template #filter>
              <MultiSelectFilter ref="state"
                filter-field="state"
                option-value="code"
                class="form-control form-control-select"
                option-label="name"
                :options="stateOptions"
                @change="onChange" />
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <router-link :to="{ name: 'admin_communication_template_categories_edit', params: { id: slotProps.data.id } }"
                class="action btn btn-fill btn-outline-primary mx-2">
                Edit
              </router-link>
            </template>
          </Column>
        </template>
      </PortalTable>
    </template>
  </CardSection>
</template>
<script>
import CardSection from "@/src/components/shared/CardSection.vue";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import { useTemplateStore } from '@/src/stores/template';
import { storeToRefs } from 'pinia';
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import { pageMixin } from "@/src/mixins/pageMixin";
import { paginationMixin } from "@/src/mixins/paginationMixin";
import InputText from 'primevue/inputtext';
import { FilterMatchMode } from '@primevue/core/api';
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";

export default {
  components: {
    CardSection,
    PortalTable,
    Column,
    InputText,
    MultiSelectFilter
  },
  mixins: [ savableStateMixin, pageMixin, paginationMixin ],
  setup() {
    const templateStore = useTemplateStore();
    const { getCategoryTypes, translateCategoryType } = storeToRefs(templateStore)
    return {
      templateStore, getCategoryTypes, translateCategoryType
    }
  },
  data() {
    return {
      filterValues: {
        "category_name" : { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "category_type" : { value: null, matchMode: FilterMatchMode.IN },
        "state" : { value: ['enabled'], matchMode: FilterMatchMode.IN }
      },
      stateOptions: [
        { code: "enabled", name: "Enabled" },
        { code: "disabled", name: "Disabled" }
      ]
    }
  },
  async mounted() {
    try {
      this.setSavableStateLoading();
      await this.templateStore.loadCategoryTypes();
      this.setSavableStateIdle();
    } catch(error) {
      this.handleMajorError(error);
    }

    const query = this.$route.query;
    const hasUrlFilters = Object.keys(query).length > 0;
    if (!hasUrlFilters) {
      this.searchParams = { state: ['enabled'] }; // set default state filter for data load if none in URL.
    }
    await this.getRowData();
    if (!hasUrlFilters) {
      this.$nextTick(() => {
        this.$refs.state?.setFilters(['enabled']); // ensure default state filter applied to UI after data load if none in URL.
      });
    }
  },
  methods: {
    async getRowData(reset = false) {
      this.setSavableStateProcessing();
      if (reset) this.pageNumber = 1;

      let params = {
        page: this.pageNumber,
        sort_by: this.currentSortField,
        sort_type: this.currentSortType,
      };

      const searchWithDefaults = { state: ['enabled'], ...this.searchParams };
      params = { ...searchWithDefaults, ...params };

      try {
        const response = await this.templateStore.loadPaginatedCategories(params);
        this.processPagyResponse(response);
        this.setSavableStateIdle();
      } catch(error) {
        this.handleMajorError(error);
      }
    },
    onChange(event) {
      this.$refs.template_categories_index.resetFilters(event);
    }
  }
}
</script>
